import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Dialog, EmptyState, Field, Select, Spinner, Switch, type Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { Table, td, tdR, th, thR } from '../components/reports/blocks';
import { downloadReport } from '../components/reports/download';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  ReportExportList,
  ReportScheduleList,
  SavedReportList,
  ScheduleRecipients,
} from '../lib/types-faz8';

/**
 * Rapor dosyalarım ve zamanlanmış raporlar (F8-10, ADR-810). Dosyalar yalnızca sahibine görünür ve 30 gün saklanır;
 * zamanlanmış rapor e-postası yalnızca bu sayfanın bağlantısını içerir. Rehber:
 * docs/faz-8/REPORTS_FINANCE_TR.md#rapor-dosyalari.
 */

const STATUS_TONE: Record<string, Tone> = { queued: 'slate', running: 'amber', done: 'moss', failed: 'rose' };
const DELIVERY_TONE: Record<string, Tone> = {
  running: 'amber',
  sent: 'moss',
  partial: 'amber',
  failed: 'rose',
};
const KNOWN_ERRORS = new Set([
  'report_member_inactive',
  'report_export_too_large',
  'no_eligible_recipient',
  'forbidden',
  'report_export_not_ready',
]);

function useErrorText() {
  const t = useT();
  return (code: string | null) =>
    code && KNOWN_ERRORS.has(code) ? t(`errors.${code}`) : t('admin.reportFiles.failedGeneric');
}

function useWhen() {
  const t = useT();
  return (s: { frequency: string; weekday: number | null; monthDay: number | null; hour: number }) => {
    const hour = `${String(s.hour).padStart(2, '0')}:00`;
    if (s.frequency === 'weekly')
      return t('admin.reportFiles.when.weekly', {
        day: t(`admin.reportFiles.weekdaysInline.${s.weekday}`),
        hour,
      });
    if (s.frequency === 'monthly') return t('admin.reportFiles.when.monthly', { day: s.monthDay ?? 1, hour });
    return t('admin.reportFiles.when.daily', { hour });
  };
}

export function ReportFilesPage() {
  const { can } = usePermissions();
  const search = useSearch({ from: '/app/rapor-dosyalari' });
  return (
    <PageFrame helpKey="page.report_files" wide>
      <Files />
      {can('reports.schedule.manage') && <Schedules preselect={search.zamanla ?? null} />}
    </PageFrame>
  );
}

function Files() {
  const t = useT();
  const { formatDate, formatNumber } = useI18n();
  const errorText = useErrorText();
  const q = useQuery({
    queryKey: ['report-exports'],
    queryFn: () => api.get<ReportExportList>('/v1/admin/reports/exports'),
    // Sırada veya hazırlanan dosya varken birkaç saniyede bir yenilenir.
    refetchInterval: (query) =>
      query.state.data?.some((x) => x.status === 'queued' || x.status === 'running') ? 3000 : false,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const list = q.data ?? [];
  return (
    <section aria-labelledby="files-title">
      <h2 id="files-title" className="mb-1 text-lg font-semibold text-ink">
        {t('admin.reportFiles.filesTitle')}
      </h2>
      <p className="mb-3 text-sm text-ink-3">{t('admin.reportFiles.keepNote')}</p>
      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !list.length ? (
        <EmptyState
          title={t('admin.reportFiles.filesEmpty')}
          action={
            <Link to="/rapor-olusturucu" className="font-medium text-pine hover:underline">
              {t('admin.reportWidgets.goBuilder')}
            </Link>
          }
        />
      ) : (
        <Table
          caption={t('admin.reportFiles.filesTitle')}
          head={
            <tr>
              <th scope="col" className={th}>
                {t('admin.reportFiles.name')}
              </th>
              <th scope="col" className={th}>
                {t('admin.reportFiles.source')}
              </th>
              <th scope="col" className={th}>
                {t('admin.reportFiles.status')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.reportFiles.rows')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.reportFiles.size')}
              </th>
              <th scope="col" className={th}>
                {t('admin.reportFiles.created')}
              </th>
              <th scope="col" className={th}>
                {t('admin.reportFiles.expires')}
              </th>
              <th scope="col" className={th}>
                <span className="sr-only">{t('admin.reportFiles.download')}</span>
              </th>
            </tr>
          }
        >
          {list.map((x) => (
            <tr key={x.id} className="border-t border-line">
              <th scope="row" className={`${td} text-start font-medium`}>
                {x.name} <span className="font-normal text-ink-3">· {x.format.toUpperCase()}</span>
              </th>
              <td className={td}>{t(`admin.reportFiles.sources.${x.source}`)}</td>
              <td className={td}>
                <Badge tone={STATUS_TONE[x.status] ?? 'neutral'}>
                  {t(`admin.reportFiles.statuses.${x.status}`)}
                </Badge>
                {x.status === 'failed' && <p className="mt-1 text-xs text-rose">{errorText(x.error)}</p>}
              </td>
              <td className={tdR}>{x.rows === null ? '—' : formatNumber(x.rows)}</td>
              <td className={tdR}>
                {x.sizeBytes === null
                  ? '—'
                  : t('admin.reportFiles.kb', {
                      n: formatNumber(Math.max(1, Math.round(x.sizeBytes / 1024))),
                    })}
              </td>
              <td className={td}>{formatDate(x.createdAt)}</td>
              <td className={td}>{x.expiresAt ? formatDate(x.expiresAt, false) : '—'}</td>
              <td className={td}>
                {x.status === 'done' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={busy === x.id}
                    aria-label={t('admin.reportFiles.downloadNamed', { name: x.name })}
                    onClick={async () => {
                      setBusy(x.id);
                      await downloadReport(
                        `/v1/admin/reports/exports/${x.id}/download`,
                        x.fileName ?? `rapor.${x.format}`,
                      );
                      setBusy(null);
                    }}
                  >
                    {t('admin.reportFiles.download')}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </section>
  );
}

type Schedule = ReportScheduleList[number];

function Schedules({ preselect }: { preselect: string | null }) {
  const t = useT();
  const { formatDate } = useI18n();
  const when = useWhen();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<{ edit: Schedule | null; savedReportId: string | null } | null>(
    preselect ? { edit: null, savedReportId: preselect } : null,
  );
  const q = useQuery({
    queryKey: ['report-schedules'],
    queryFn: () => api.get<ReportScheduleList>('/v1/admin/reports/schedules'),
  });
  const toggle = useApiMutation(
    (s: Schedule) =>
      api.patch(`/v1/admin/reports/schedules/${s.id}`, { enabled: !s.enabled }, { ifMatch: s.version }),
    { invalidate: [['report-schedules']] },
  );
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/reports/schedules/${id}`), {
    invalidate: [['report-schedules']],
    success: t('admin.reportFiles.deleted'),
  });
  const close = () => {
    setDialog(null);
    if (preselect) void navigate({ to: '/rapor-dosyalari', search: {}, replace: true });
  };
  const list = q.data ?? [];
  return (
    <section aria-labelledby="schedules-title">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 id="schedules-title" className="flex items-center gap-1 text-lg font-semibold text-ink">
          {t('admin.reportFiles.schedulesTitle')}
          <HelpTip k="report_files.schedule" />
        </h2>
        <Button variant="primary" onClick={() => setDialog({ edit: null, savedReportId: null })}>
          {t('admin.reportFiles.newSchedule')}
        </Button>
      </div>
      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !list.length ? (
        <EmptyState title={t('admin.reportFiles.schedulesEmpty')} />
      ) : (
        <Table
          caption={t('admin.reportFiles.schedulesTitle')}
          head={
            <tr>
              <th scope="col" className={th}>
                {t('admin.reportFiles.report')}
              </th>
              <th scope="col" className={th}>
                {t('admin.reportFiles.frequency')}
              </th>
              <th scope="col" className={th}>
                {t('admin.reportFiles.recipients')}
              </th>
              <th scope="col" className={th}>
                {t('admin.reportFiles.enabled')}
              </th>
              <th scope="col" className={th}>
                {t('admin.reportFiles.nextRun')}
              </th>
              <th scope="col" className={th}>
                {t('admin.reportFiles.lastDelivery')}
              </th>
              <th scope="col" className={th}>
                <span className="sr-only">{t('admin.reportFiles.edit')}</span>
              </th>
            </tr>
          }
        >
          {list.map((s) => (
            <tr key={s.id} className="border-t border-line">
              <th scope="row" className={`${td} text-start font-medium`}>
                {s.reportName} <span className="font-normal text-ink-3">· {s.format.toUpperCase()}</span>
              </th>
              <td className={td}>{when(s)}</td>
              <td className={td}>
                {t('admin.reportFiles.recipientCount', { count: s.recipientIds.length })}
              </td>
              <td className={td}>
                <span className="inline-flex items-center gap-2">
                  <Switch
                    checked={s.enabled}
                    disabled={toggle.isPending}
                    label={t('admin.reportFiles.enabledNamed', { name: s.reportName })}
                    onCheckedChange={() => toggle.mutate(s)}
                  />
                  <span className="text-sm text-ink-2">
                    {s.enabled ? t('admin.reportFiles.on') : t('admin.reportFiles.off')}
                  </span>
                </span>
              </td>
              <td className={td}>{s.enabled && s.nextRunAt ? formatDate(s.nextRunAt) : '—'}</td>
              <td className={td}>
                {s.lastDelivery ? (
                  <>
                    <Badge tone={DELIVERY_TONE[s.lastDelivery.status] ?? 'neutral'}>
                      {t(`admin.reportFiles.deliveryStatuses.${s.lastDelivery.status}`)}
                    </Badge>
                    <p className="mt-1 text-xs text-ink-3">
                      {t('admin.reportFiles.deliveryCounts', {
                        sent: s.lastDelivery.sentCount,
                        skipped: s.lastDelivery.skippedCount,
                      })}
                    </p>
                    {s.lastDelivery.status === 'failed' && (
                      <p className="text-xs text-rose">{errorText(s.lastDelivery.error)}</p>
                    )}
                  </>
                ) : (
                  <span className="text-ink-3">{t('admin.reportFiles.never')}</span>
                )}
              </td>
              <td className={td}>
                <span className="inline-flex gap-1">
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-label={t('admin.reportFiles.editNamed', { name: s.reportName })}
                    onClick={() => setDialog({ edit: s, savedReportId: s.savedReportId })}
                  >
                    {t('admin.reportFiles.edit')}
                  </Button>
                  <ConfirmButton
                    message={t('admin.reportFiles.deleteConfirm')}
                    onConfirm={() => remove.mutateAsync(s.id)}
                  >
                    <span aria-hidden>{t('common.delete')}</span>
                    <span className="sr-only">
                      {t('admin.reportFiles.deleteNamed', { name: s.reportName })}
                    </span>
                  </ConfirmButton>
                </span>
              </td>
            </tr>
          ))}
        </Table>
      )}
      {dialog && <ScheduleDialog edit={dialog.edit} savedReportId={dialog.savedReportId} onClose={close} />}
    </section>
  );
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MONTH_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

function ScheduleDialog({
  edit,
  savedReportId,
  onClose,
}: {
  edit: Schedule | null;
  savedReportId: string | null;
  onClose: () => void;
}) {
  const t = useT();
  const [idem] = useState(() => new IdempotencyKey());
  const [reportId, setReportId] = useState(savedReportId ?? '');
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>(edit?.frequency ?? 'weekly');
  const [weekday, setWeekday] = useState(edit?.weekday ?? 1);
  const [monthDay, setMonthDay] = useState(edit?.monthDay ?? 1);
  const [hour, setHour] = useState(edit?.hour ?? 8);
  const [format, setFormat] = useState<'csv' | 'xlsx'>(edit?.format ?? 'xlsx');
  const [recipients, setRecipients] = useState<string[]>(edit?.recipientIds ?? []);
  const [enabled, setEnabled] = useState(edit?.enabled ?? false);
  const reports = useQuery({
    queryKey: ['saved-reports'],
    queryFn: () => api.get<SavedReportList>('/v1/admin/reports/custom'),
  });
  const members = useQuery({
    queryKey: ['report-schedule-recipients'],
    queryFn: () => api.get<ScheduleRecipients>('/v1/admin/reports/schedules/recipients'),
  });
  const save = useApiMutation(
    () => {
      const body = {
        frequency,
        weekday: frequency === 'weekly' ? weekday : null,
        monthDay: frequency === 'monthly' ? monthDay : null,
        hour,
        format,
        recipientIds: recipients,
        enabled,
      };
      return edit
        ? api.patch(`/v1/admin/reports/schedules/${edit.id}`, body, { ifMatch: edit.version })
        : api.post(
            '/v1/admin/reports/schedules',
            { ...body, savedReportId: reportId },
            { idempotencyKey: idem },
          );
    },
    {
      invalidate: [['report-schedules']],
      success: t('admin.reportFiles.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const toggleRecipient = (id: string) =>
    setRecipients((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const recipientsError =
    fieldError(save.error, 'recipientIds') ??
    recipients.map((_, i) => fieldError(save.error, `recipientIds.${i}`)).find(Boolean);
  const editName = edit?.reportName;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={edit ? t('admin.reportFiles.dialogEdit') : t('admin.reportFiles.dialogNew')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!reportId || !recipients.length}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {edit ? (
          <p className="text-sm font-medium text-ink">{editName}</p>
        ) : (
          <Field
            label={t('admin.reportFiles.report')}
            required
            error={fieldError(save.error, 'savedReportId')}
          >
            {(p) =>
              reports.data && !reports.data.length ? (
                <p className="text-sm text-ink-3">{t('admin.reportFiles.noSavedReports')}</p>
              ) : (
                <Select {...p} value={reportId} onChange={(e) => setReportId(e.target.value)}>
                  <option value="">{t('admin.reportFiles.pickReport')}</option>
                  {(reports.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                  {reportId && reports.data && !reports.data.some((r) => r.id === reportId) && (
                    <option value={reportId}>{t('admin.reportFiles.unknownReport')}</option>
                  )}
                </Select>
              )
            }
          </Field>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('admin.reportFiles.frequency')} help={<HelpTip k="report_files.schedule" />}>
            {(p) => (
              <Select
                {...p}
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as 'daily' | 'weekly' | 'monthly')}
              >
                {(['daily', 'weekly', 'monthly'] as const).map((f) => (
                  <option key={f} value={f}>
                    {t(`admin.reportFiles.frequencies.${f}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {frequency === 'weekly' && (
            <Field label={t('admin.reportFiles.weekday')} error={fieldError(save.error, 'weekday')}>
              {(p) => (
                <Select {...p} value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                    <option key={d} value={d}>
                      {t(`admin.reportFiles.weekdays.${d}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {frequency === 'monthly' && (
            <Field label={t('admin.reportFiles.monthDay')} error={fieldError(save.error, 'monthDay')}>
              {(p) => (
                <Select {...p} value={monthDay} onChange={(e) => setMonthDay(Number(e.target.value))}>
                  {MONTH_DAYS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <Field label={t('admin.reportFiles.hour')}>
            {(p) => (
              <Select {...p} value={hour} onChange={(e) => setHour(Number(e.target.value))}>
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {`${String(h).padStart(2, '0')}:00`}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.reportFiles.format')}>
            {(p) => (
              <Select {...p} value={format} onChange={(e) => setFormat(e.target.value as 'csv' | 'xlsx')}>
                <option value="xlsx">XLSX</option>
                <option value="csv">CSV</option>
              </Select>
            )}
          </Field>
        </div>
        <p className="text-sm text-ink-3">{t(`admin.reportFiles.periodNote.${frequency}`)}</p>
        <fieldset>
          <legend className="mb-1 flex items-center gap-1 text-sm font-medium text-ink">
            {t('admin.reportFiles.recipients')}
            <HelpTip k="report_files.recipients" />
          </legend>
          {!members.data ? (
            <Spinner label={t('common.loading')} />
          ) : (
            <div className="grid max-h-48 gap-1 overflow-y-auto sm:grid-cols-2">
              {members.data.map((m) => (
                <label key={m.userId} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={recipients.includes(m.userId)}
                    onChange={() => toggleRecipient(m.userId)}
                  />
                  {m.name}
                </label>
              ))}
            </div>
          )}
          {recipientsError && (
            <p role="alert" className="mt-1 text-sm text-rose">
              {recipientsError}
            </p>
          )}
        </fieldset>
        <div className="flex items-center gap-1">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            {t('admin.reportFiles.enabled')}
          </label>
          <HelpTip k="report_files.enabled" />
        </div>
        <p className="rounded-md border border-line bg-surface-2 p-3 text-xs text-ink-2">
          {t('admin.reportFiles.deliveryNote')}
        </p>
      </div>
    </Dialog>
  );
}
